import React, { useState, useEffect } from 'react';
import axios from 'axios';
import loadable from '@loadable/component';
import { Helmet } from "react-helmet";
import { sharedLoggerApi } from 'shared-helpers';

// api
import privateEquityApi from '/src/apis/privateEquityApi';

// constants
import { HUBSPOT_FORM_SOURCE } from '/src/lib/constants';
import { WEB_URL, PUBLIC_IMG_URL } from '/src/lib/endpoints';
import { SALES_CONTACT_SUCCESS_MESSAGE } from '/src/lib/messages';

// helpers
import AmplitudeHelperClass from '/src/lib/amplitudeHelper';
import { formatCount, withBootstrapPlugin } from '/src/lib/helpers';
import { handleDemoRequest } from '/src/lib/helpers/demoRequestHelpers';
import { handleRedirectMessage } from '/src/components/Reusable/toast';

// components
import ContactSalesPopup from '/src/components/ContactForms/ContactSalesPopup';
const SuccessPopup = loadable(() => import(/* webpackChunkName: "SuccessPopup" */ '/src/components/Reusable/SuccessPopup'));



const amplitudeHelper = new AmplitudeHelperClass();
const LoggerApi = sharedLoggerApi;

export default function PrivateEquityLanding(props) {

    // helmet info
    const helmet = {
        page_title: 'Discover Private Equity Data | MAGNiTT',
        page_description: 'MAGNiTT provides a full list of private equity in the Middle East and North Africa. Discover private equity in different industries and the investments made.',
        page_url: '/private-equity',
    }

    const [count, setCount] = useState({ PE_Transactions: '', PE_Companies: '', PE_Investors: '' });
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [demoRequestSuccess, setDemoRequestSuccess] = useState(false);
    const componentName = 'PrivateEquityLanding';

    const showContactForm = () => {
        amplitudeHelper.trackEvent('Clicked Contact Sales from Landing Page', {
            landing_page_type: 'Private Equity'
        });
        withBootstrapPlugin('modal', ($) => $("#contactFormModal").modal('show'));
    }

    useEffect(() => {
        // Check for redirect params without showing toast on landing page
        handleRedirectMessage(props.locationSearch, false);

        getEntityCounts();
    }, []);

    const getEntityCounts = () => {
        const countTypes = ["PE_Transactions", "PE_Companies", "PE_Investors"];
        countTypes.map((entity, v) => {

            let countAPI = null;
            if (entity === "PE_Transactions") {
                countAPI = privateEquityApi.getPETransactionsCount();
            } else if (entity === "PE_Companies") {
                countAPI = privateEquityApi.getStaticPECompanyCount();
            } else if (entity === "PE_Investors") {
                countAPI = privateEquityApi.getPEInvestorsCount();
            }

            countAPI.then(response => {
                setCount(prev => ({
                    ...prev,
                    [entity]: formatCount(response)
                }))
            }).catch(data => {
                // error, sets a default count
                setCount(prev => ({
                    ...prev,
                    [entity]: data.totalCount
                }))
                LoggerApi.logError("getEntityCount-" + entity, componentName, data.error)
            })

        })
    }

    const handleRequestDemo = async (e) => {
        const hubspotFormSource = HUBSPOT_FORM_SOURCE.privateequity_demo;
        handleDemoRequest(email, hubspotFormSource).then(response => {
            if (response.type === 'success') {
                setEmail('');
                setEmailError(false);
                setDemoRequestSuccess(true);
            } else if (response.type === 'error') {
                setEmailError(response.message);
                setDemoRequestSuccess(false);
            }
        })
    }

    const imageLink = 'https://d2p9i44hnkrmkx.cloudfront.net/files/research-image/2422025/2628546140471-MENA_PE_LP.png';
    const reportLink = 'https://magnitt.com/research/mena-pe-report-50979?utm_source=productpage&utm_medium=marketing&utm_campaign=2025flagshipreports';

    return (
        <div className="landing-page footer-margin-offset">

            <Helmet
                title={helmet.page_title}
                link={[
                    {
                        rel: 'canonical',
                        href: `${WEB_URL}${helmet.page_url}`,
                    },
                ]}
                meta={[
                    {
                        name: 'description',
                        content: helmet.page_description,
                    },
                    {
                        name: 'abstract',
                        content: helmet.page_description,
                    },
                    {
                        property: 'og:type',
                        content: 'article',
                    },
                    {
                        property: 'og:url',
                        content: `${WEB_URL}${helmet.page_url}`,
                    },
                    {
                        property: 'og:title',
                        content: helmet.page_title,
                    },
                ]}
                script={[
                    {
                        type: 'application/ld+json',
                        innerHTML: `{
                                "@context": "http://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [{
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "private equity",
                                    "item": "${WEB_URL}/private-equity"
                                }]
                            }`,
                    },
                ]}
            />

            <div className="section-title">
                <div className="section-title-box">
                    <div className="page-header">
                        <h2>The Leading VC Data Platform for Emerging Markets — Now Covering <span>Private Equity</span></h2>
                        <p>We’ve expanded beyond VC — now offering private equity data to help you stay ahead of the market</p>
                    </div>
                </div>
            </div>

            <div className="section-video" data-pe-landing="true">
                <div className="row">
                    <div className="col-lg-6 col-xl-7">
                        <div className="section-video-content">
                            <video autoPlay muted loop playsInline className="video-with-border">
                                <source src={`${PUBLIC_IMG_URL}/videos/pe-landing.mp4`} type="video/mp4" />
                                {/* fallback img is displayed in the split second before the video loads */}
                                <img src={`${PUBLIC_IMG_URL}/videos/pe-landing-loader.jpg`} title="Your browser does not support the <video> tag"></img>
                            </video>
                        </div>
                    </div>
                    <div className="col-lg-6 col-xl-5">
                        <div className="section-video-text">

                            <h6 className="section-video-heading">
                                What You’ll Get
                            </h6>

                            {/* {" "} spacing is needed for mobile */}
                            <p>From private equity fund launches to buyouts, growth investments, and LP insights — get the data investors have been missing.</p>
                            <p>{" "}At MAGNiTT, we make your life easier with accessible, verified, and easy-to-use data for your research and due diligence.</p>
                            <p>{" "}Maximize your subscription by exploring our latest feature, or reach out to unlock full access to all our directories!</p>

                            <a className="btn btn-main" onClick={() => showContactForm()}>
                                Speak to our Sales Team!
                            </a>

                        </div>
                    </div>
                </div>
            </div>

            <div className="section-feature-counts">
                <p className="landing-section-header">
                    Get verified <span className="red">real-time</span> data
                </p>
                <div className="row">
                    <div className="col-sm-6 col-md-4 col-lg-3">
                        <div className="feature-icon-box">
                            <i className="fas fa-building"></i>
                            <span>Analyze <b>{count.PE_Companies}</b> company profiles with private equity investment</span>
                        </div>
                    </div>
                    <div className="col-sm-6 col-md-4 col-lg-3">
                        <div className="feature-icon-box">
                            <i className="fas fa-search-dollar"></i>
                            <span>Review <b>{count.PE_Transactions}</b> transactions by private equity firms and corporates</span>
                        </div>
                    </div>
                    <div className="col-sm-6 col-md-4 col-lg-3">
                        <div className="feature-icon-box">
                            <i className="fas fa-file-invoice-dollar"></i>
                            <span>Identify <b>{count.PE_Investors}</b> private equity investors</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-image-promo" data-pe-landing="true">
                <div className="row">
                    <div className="col-lg-4">
                        <a href={reportLink} target="_blank">
                            <img src={imageLink} />
                        </a>
                    </div>
                    <div className="col-lg-8">
                        <div className="image-promo-content">
                            <p className="landing-section-header">
                                Unlock key insights into the MENA private equity landscape with our latest free report!
                            </p>
                            <p>Dive into 5 years of private equity data, from deal volume and buyouts to top markets, industries, and leading players — offering a definitive review of the region’s evolving private equity ecosystem.</p>
                            <a className="btn btn-main-outline btn-lg" href={reportLink} target="_blank">
                                Explore the Report!
                                <i className="fas fa-mouse-pointer"></i>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-get-demo">
                <p className="landing-section-header">
                    Book a FREE 15-minute Demo TODAY!
                </p>
                <div className="demo-subtitle">
                    Learn how our verified Venture Capital & Private Equity data can inform your investment strategy
                </div>

                <div className='demo-input'>
                    <div className="input-group row m-0" >
                        <input type="text"
                            className="form-control col-9 clear-outline"
                            placeholder='Enter your email'
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setEmailError(false) }}
                        />
                        <a className="btn btn-link input-group-append staticpage-demo-btn col-3"
                            onClick={handleRequestDemo}>
                            Submit
                        </a>
                    </div>
                    {emailError &&
                        <div className="form-error">{emailError}</div>
                    }
                    {demoRequestSuccess &&
                        <div className="form-success">Your request was submitted, you will be contacted shortly!</div>
                    }
                </div>
            </div>

            {/* modals */}
            <SuccessPopup
                msg={SALES_CONTACT_SUCCESS_MESSAGE}
                handleClose={() => { }}
            />
            <ContactSalesPopup page={HUBSPOT_FORM_SOURCE.sales_privateequity} />

        </div>
    )
}


